// Shell view-model – odvozený stav pro prezentační vrstvu shellu

import type { NavigationState } from '../core/navigationState';
import type { LayerDescriptor } from '../core/layerDescriptor';
import type { BreadcrumbEntry, LayoutMode } from '../core/navigationTypes';
import type { AgendaRegistry } from '../core/agendaRegistry';

/** Render target jedné vrstvy v shell projekci */
export type LayerRenderTarget = 'root-content' | 'list-pane' | 'detail-panel' | 'overlay-window';

/** Projekce jedné vrstvy pro shell rendering */
export interface LayerProjection {
  readonly layer: LayerDescriptor;
  readonly renderTarget: LayerRenderTarget;
  readonly title: string;
}

/** Projekce detailového panelu pro list-detail režim */
export interface DetailPanelProjection {
  readonly layer: LayerDescriptor;
  readonly title: string;
  /** Počet zkolabovaných předchozích detail kroků (pro browser Back) */
  readonly collapsedCount: number;
}

/** Projekce overlay okna */
export interface WindowProjection {
  readonly layer: LayerDescriptor;
  readonly title: string;
  /** Index v overlay stacku (0 = první overlay) – pro odsazení */
  readonly stackIndex: number;
}

/** View-model pro navigační shell */
export interface NavigationShellViewModel {
  /** Poslední root vrstva – základní obsah */
  readonly activeRoot: LayerDescriptor | null;
  /** Modální vrstvy za aktivním rootem */
  readonly activeModals: readonly LayerDescriptor[];
  /** Vrchní interaktivní vrstva */
  readonly activeTop: LayerDescriptor | null;
  /** Breadcrumb navigační cesta */
  readonly breadcrumbs: readonly BreadcrumbEntry[];
  /** Má aktivní modály */
  readonly hasModals: boolean;
  /** Detail panel pro list-detail režim (null pokud overlay nebo bez detailu) */
  readonly detailPanel: DetailPanelProjection | null;
  /** Overlay window vrstvy (vrstvy, které se renderují jako modální okna přes celý viewport) */
  readonly overlayWindows: readonly WindowProjection[];
  /** Layout policy aktivního rootu (undefined pokud root nemá shell hint nebo není root) */
  readonly layoutMode: LayoutMode | undefined;
}

/**
 * Odvodí shell view-model z navigačního stavu.
 * Shell neparsuje URL ani nepočítá stack sám.
 */
export function deriveShellViewModel(
  state: NavigationState,
  registry?: AgendaRegistry
): NavigationShellViewModel {
  const { layers, activeTopLayerId } = state;

  const activeTop = layers.find((l) => l.layerId === activeTopLayerId) ?? null;

  // Najdi první root (viewport root) a poslední root (potenciální overlay root)
  let firstRootIndex = -1;
  let lastRootIndex = -1;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.presentation === 'root') {
      if (firstRootIndex === -1) firstRootIndex = i;
      lastRootIndex = i;
    }
  }

  const hasOverlayRoot = firstRootIndex !== lastRootIndex && firstRootIndex >= 0;

  // activeRoot je první root – ten definuje viewport a layout policy
  const activeRoot = firstRootIndex >= 0 ? (layers[firstRootIndex] ?? null) : null;

  // Modály mezi prvním rootem a overlay rootem (nebo do konce, pokud overlay root neexistuje)
  // Pokud žádný root neexistuje, všechny vrstvy jsou "rootless" modály
  const modalEndIndex = hasOverlayRoot ? lastRootIndex : layers.length;
  const activeModals =
    firstRootIndex >= 0
      ? layers.slice(firstRootIndex + 1, modalEndIndex)
      : layers.slice(0, modalEndIndex);

  // Resolve root layout policy z prvního rootu
  const rootConfig = activeRoot && registry ? registry.getById(activeRoot.agendaId) : undefined;
  const layoutMode = rootConfig?.presentationHints?.shell?.layoutMode;
  const allowlist = rootConfig?.presentationHints?.shell?.detailAgendaAllowlist ?? [];
  const isListDetail = layoutMode === 'list-detail';

  // Classify modals of the first root into detail-compatible and overlay fallback
  // Detail-compatible layers only go to the detail panel if they come BEFORE any
  // incompatible modal. Once an incompatible modal appears, all subsequent layers
  // (even detail-compatible ones) render as overlay windows.
  let detailPanel: DetailPanelProjection | null = null;
  const overlayWindows: WindowProjection[] = [];
  const detailCompatibleLayers: LayerDescriptor[] = [];
  let detailZoneEnded = false;

  for (const modal of activeModals) {
    if (!detailZoneEnded && isListDetail && allowlist.includes(modal.agendaId)) {
      detailCompatibleLayers.push(modal);
    } else {
      detailZoneEnded = true;
      const config = registry?.getById(modal.agendaId);
      overlayWindows.push({
        layer: modal,
        title: resolveTitle(modal, config),
        stackIndex: overlayWindows.length,
      });
    }
  }

  // Last detail-compatible layer is the visible detail panel
  if (detailCompatibleLayers.length > 0) {
    const lastDetail = detailCompatibleLayers[detailCompatibleLayers.length - 1];
    if (lastDetail) {
      const config = registry?.getById(lastDetail.agendaId);
      detailPanel = {
        layer: lastDetail,
        title: resolveTitle(lastDetail, config),
        collapsedCount: detailCompatibleLayers.length - 1,
      };
    }
  }

  // Overlay root a jeho modály → všechny jako overlay windows
  if (hasOverlayRoot) {
    const overlayRootAndModals = layers.slice(lastRootIndex);
    for (const layer of overlayRootAndModals) {
      const config = registry?.getById(layer.agendaId);
      overlayWindows.push({
        layer,
        title: resolveTitle(layer, config),
        stackIndex: overlayWindows.length,
      });
    }
  }

  // Breadcrumbs – celá cesta od prvního rootu přes všechny vrstvy
  // Pokud root neexistuje, breadcrumbs zahrnují všechny vrstvy
  // In list-detail mode: collapsed details are omitted, only visible detail stays
  const breadcrumbs: BreadcrumbEntry[] = [];
  const visibleLayers = firstRootIndex >= 0 ? layers.slice(firstRootIndex) : layers;
  const collapsedLayerIds = new Set(detailCompatibleLayers.slice(0, -1).map((l) => l.layerId));

  for (const layer of visibleLayers) {
    if (collapsedLayerIds.has(layer.layerId)) continue;
    const config = registry?.getById(layer.agendaId);
    const title = resolveTitle(layer, config);

    breadcrumbs.push({
      layerId: layer.layerId,
      agendaId: layer.agendaId,
      title,
      presentation: layer.presentation,
      icon: config?.icon,
    });
  }

  // hasModals zahrnuje modály prvního rootu + overlay root vrstvy
  const totalModals = activeModals.length + (hasOverlayRoot ? layers.length - lastRootIndex : 0);

  return {
    activeRoot,
    activeModals,
    activeTop,
    breadcrumbs,
    hasModals: totalModals > 0,
    detailPanel,
    overlayWindows,
    layoutMode,
  };
}

export function resolveTitle(
  layer: LayerDescriptor,
  config?: { title?: string | ((params: Record<string, unknown>) => string) }
): string {
  if (!config?.title) return layer.agendaId;
  if (typeof config.title === 'string') return config.title;
  return config.title(layer.openParams);
}
